import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DraftBundleInfoModal.module.css';
import DraftBankingDetailInfoComponent from './DraftBankingDetailInfoComponent';
import DraftOrganoidDetailInfoComponent from './DraftOrganoidDetailInfoComponent';
import DraftPatientDetailInfoComponent from './DraftPatientDetailInfoComponent';
import DraftCommentComponent from '../subComponets/DraftCommentComponent';
import EditBanking from '../../components/modal/EditBanking';

// [2026-09-14] 전체 승인 권한의 하한. 백엔드 managemnet_info.js 와 같은 값으로 유지할 것.
const APPROVE_MIN_AUTH_LEVEL = 4;

// 백엔드 managemnet_info.js 의 FORCE_APPROVERS 와 반드시 같게 유지할 것.
const FORCE_APPROVERS = [
    'mspark@gradiantbio.com',     // 박미선 팀장
    'junsoodark@gradiantbio.com', // 박준수
];

const AUTH_LEVEL_LABELS = {
    1: '읽기', 2: '추가', 3: '수정', 4: '삭제', 5: '반입', 6: '반출',
};

const STATUS_LABELS = {
    approved: '승인', pending: '대기중', canceled: '취소', rejected: '반려',
};

const CATEGORY_LABELS = {
    Banking_Info: 'Working Stock',
    Organoid_Info: 'Master Stock',
    Patient_Info: 'Patient info',
    StorageList: 'Storage',
};

const postJson = (url, body) => fetch(url, {
    method: 'post',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
}).then(r => r.json());

const formatDateTime = (dateTimeString) =>
    new Date(dateTimeString).toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });

const hashmark = (str) => str ? str.replace('hashmark', '#') : str;

// 승인 상태 배지 (좌/우 탭에서 동일하게 2번 쓰임)
function StatusBadge({ status, t }) {
    if (!status) return null;
    return (
        <span className={`${styles.approvalIcon} ${styles[status] || ''}`}>
            {STATUS_LABELS[status] ? t(STATUS_LABELS[status]) : ''}
        </span>
    );
}

function DraftBundleInfoModal({ show, onClose, selectedDraft, reDrawView }) {
    const { t } = useTranslation();

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [visibleEditBankingModal, setVisibleEditBankingModal] = useState(false);
    const [userName, setUserName] = useState('');
    const [userInfo, setUserInfo] = useState({});

    const closeEditBankingModal = () => setVisibleEditBankingModal(false);

    // 현재 유저 정보
    useEffect(() => {
        postJson('/api/getLoginedUserInfo', undefined).then(data => {
            setUserName(data.data[0]?.userName);
            setUserInfo(data?.data[0]);
        });
    }, []);

    // 댓글 조회
    const fetchComments = async () => {
        if (!selectedDraft?.draftDocumentId) return;
        try {
            const res = await fetch('/api/getComments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ draftDocumentId: selectedDraft.draftDocumentId }),
            });
            if (!res.ok) throw new Error('댓글을 불러오는 데 실패했습니다.');
            setComments(await res.json());
        } catch (err) {
            console.error('댓글 목록 조회 실패:', err);
        }
    };

    // 모달 열림 시: 댓글 로드 + body 스크롤 잠금
    useEffect(() => {
        if (!show) {
            document.body.style.overflow = 'unset';
            return;
        }
        fetchComments();
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, [show, selectedDraft?.draftDocumentId]);

    const onSubmitComment = async () => {
        const trimmed = newComment.trim();
        if (!trimmed) return;
        try {
            const res = await fetch('/api/postComment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    draftDocumentId: selectedDraft.draftDocumentId,
                    content: trimmed,
                    author: userInfo.email,
                    authorName: userInfo.userName,
                }),
            });
            if (!res.ok) {
                const errJson = await res.json();
                throw new Error(errJson.message || '댓글 등록 실패');
            }
            setNewComment('');
            await fetchComments();
        } catch (error) {
            console.error('댓글 제출 중 오류:', error);
            alert(error.message);
        }
    };

    // 기안 액션 (cancel/reject/approve) — 3개 함수의 공통 패턴
    // ⚠️ approve는 번들 전용 엔드포인트 사용
    const performDraftAction = (confirmKey, url, body) => {
        if (!window.confirm(t(confirmKey))) return;
        postJson(url, body).then(data => {
            alert(data.message);
            onClose();
            reDrawView();
        });
    };

    const cancelDraft  = (id) => performDraftAction('기안을 취소하시겠습니까?', '/api/cancelDraft',           { draftDocumentId: id });
    const rejectDraft  = (id) => performDraftAction('반려하시겠습니까?',         '/api/authorizeDraft',        { draftDocumentId: id, decision: 'rejected' });
    const approveDraft = (id) => performDraftAction('승인하시겠습니까?',         '/api/authorizeBundleDraft',  { draftDocumentId: id, decision: 'approved' });

    if (!show) return null;

    const isPending  = selectedDraft?.authorizedStatus === 'pending';
    // 백엔드 authorizeBundleDraft 와 같은 3조건.
    const isApprover = userName === selectedDraft?.authorizedPersonName
        || Number(userInfo.authLevel) >= APPROVE_MIN_AUTH_LEVEL
        || FORCE_APPROVERS.includes(userInfo.email);
    const isDrafter  = selectedDraft.draftUserName === userName;

    return (
        <div className={styles.popupBackground} onClick={onClose}>
            <div className={styles.popupContent} onClick={e => e.stopPropagation()}>
                <div className={styles.draftInfoLeftTab}>
                    <div className={styles.draftInfoTitle}>
                        {selectedDraft.draftCategory && <> {t('뱅킹 스탁 일괄')} </>}
                        {selectedDraft.authLevel && (
                            <>&nbsp;{t(AUTH_LEVEL_LABELS[selectedDraft.authLevel] || '')}</>
                        )}
                        &nbsp;{t('신청')}
                        {visibleEditBankingModal && (
                            <EditBanking onClose={closeEditBankingModal} selectedDraft={selectedDraft} />
                        )}
                        <StatusBadge status={selectedDraft.authorizedStatus} t={t} />
                    </div>

                    <div className={styles.draftInfoDiv}>
                        <div className={styles.draftInfoColumn}>{t('기안 ID')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftDocumentId}</div>
                        <div className={styles.draftInfoColumn}>{t('날짜/시간')}</div>
                        <div className={styles.draftInfoData}>{formatDateTime(selectedDraft.regTime)}</div>
                        <div className={styles.draftInfoColumn}>{t('기안 타겟')}</div>
                        <div className={styles.draftInfoData}>{hashmark(selectedDraft.targetKey)}</div>
                        <div className={styles.draftInfoColumn}>{t('분류')}</div>
                        <div className={styles.draftInfoData}>{CATEGORY_LABELS[selectedDraft.draftCategory]}</div>
                        <div className={styles.draftInfoColumn}>{t('이름')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftUserName}</div>
                        <div className={styles.draftInfoColumn}>{t('팀명')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftUserTeamName}</div>
                        <div className={styles.draftInfoColumn}>{t('등록 갯수')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.bundleCount}</div>
                        <div className={styles.draftInfoColumn}>{t('기안 의견')}</div>
                        <div className={styles.draftInfoData}>
                            {selectedDraft.draftOpinion?.split('\n').map((line, idx) => (
                                <React.Fragment key={idx}>
                                    {line}<br />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {selectedDraft.draftCategory?.toLowerCase().includes('organoid') && (
                        <DraftOrganoidDetailInfoComponent
                            targetKey={selectedDraft.targetKey}
                            selectedDraft={selectedDraft}
                        />
                    )}
                    {(selectedDraft.draftCategory === 'Banking_Info' ||
                        selectedDraft.draftCategory?.toLowerCase().includes('stock')) && (
                            <DraftBankingDetailInfoComponent
                                value={selectedDraft.targetKey}
                                selectedDraft={selectedDraft}
                            />
                        )}
                    {selectedDraft.draftCategory?.toLowerCase().includes('patient') && (
                        <DraftPatientDetailInfoComponent
                            targetKey={selectedDraft.targetKey}
                            selectedDraft={selectedDraft}
                        />
                    )}
                </div>

                <div className={styles.draftInfoRightTab}>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                    <div className={styles.approval}>승인</div>
                    <div className={styles.authorizedPersonInfo}>
                        <span>{selectedDraft.authorizedPersonName}</span>
                        <StatusBadge status={selectedDraft.authorizedStatus} t={t} />
                    </div>
                    <div className={styles.authTeamName}>{selectedDraft.authorizedPersonTeamName}</div>

                    <div className={styles.commentBox}>
                        <div className={styles.commentHeader}>댓글</div>
                        <div className={styles.commentList}>
                            {comments.length > 0 ? (
                                comments.map(c => (
                                    <DraftCommentComponent
                                        key={c.id ?? c.createdAt}
                                        commentInfo={c}
                                        reDrawView={fetchComments}
                                    />
                                ))
                            ) : (
                                <div className={styles.noComments}>등록된 댓글이 없습니다.</div>
                            )}
                        </div>
                        <div className={styles.commentFooter}>
                            <textarea
                                className={styles.commentInput}
                                placeholder={t('댓글을 입력하세요...')}
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                            />
                            <button className={styles.commentSubmitButton} onClick={onSubmitComment}>
                                작성
                            </button>
                        </div>
                    </div>

                    {/* 중첩 draftButtonGroup 제거 — isPending일 때만 단일 div 렌더 */}
                    {isPending && (
                        <div className={styles.draftButtonGroup}>
                            {isApprover ? (
                                <>
                                    <button className={styles.editButton}     onClick={() => setVisibleEditBankingModal(true)}>{t('수정')}</button>
                                    <button className={styles.approvalButton} onClick={() => approveDraft(selectedDraft.draftDocumentId)}>{t('승인')}</button>
                                    <button className={styles.rejectButton}   onClick={() => rejectDraft(selectedDraft.draftDocumentId)}>{t('반려')}</button>
                                    <button className={styles.cancelButton}   onClick={() => cancelDraft(selectedDraft.draftDocumentId)}>{t('취소')}</button>
                                </>
                            ) : (isDrafter && (
                                <>
                                    <button className={styles.editButton}   onClick={() => setVisibleEditBankingModal(true)}>{t('수정')}</button>
                                    <button className={styles.cancelButton} onClick={() => cancelDraft(selectedDraft.draftDocumentId)}>{t('취소')}</button>
                                </>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DraftBundleInfoModal;